/**
 * Customer Event Tracking Service
 *
 * Batches user interaction events and sends them to the backend
 * periodically to minimize network traffic.
 *
 * Features:
 * - Event queue with batching
 * - Configurable batch size and flush interval
 * - Automatic flush on page visibility change
 * - Uses `navigator.sendBeacon` for reliability
 * - Session ID for grouping user journeys (shared with cart)
 * - Feature flag to enable/disable
 */

import { env } from '../env';
import { getSessionId } from '@/hooks/useSession';

export interface TrackedEvent {
    name: string;
    data: Record<string, unknown>;
    timestamp: string;
    sessionId: string;
    pageUrl: string;
}

interface EventBatch {
    events: TrackedEvent[];
    sentAt: string;
}

const CONFIG = {
    batchSize: 5, // Smaller batches for faster feedback
    flushIntervalMs: 10000, // 10 seconds for quicker event delivery
    maxQueueSize: 100,
    enabled: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
    debug: import.meta.env.DEV && import.meta.env.MODE !== 'test', // Keep tests quiet
    // Build the full API URL for the events endpoint
    apiUrl: `${env.API_URL}/customer/events/batch`,
};

let eventQueue: TrackedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

// Session ID is now managed by useSession.ts (shared with cart)
// This allows linking analytics events to cart actions

/**
 * Track a customer event
 *
 * @param name - Event name (e.g., 'cart_item_added')
 * @param data - Event data payload
 */
export const trackEvent = (name: string, data: Record<string, unknown> = {}): void => {
    if (!CONFIG.enabled) return;

    const event: TrackedEvent = {
        name,
        data,
        timestamp: new Date().toISOString(),
        sessionId: getSessionId(),
        pageUrl: window.location.pathname,
    };

    // Debug logging in development
    if (CONFIG.debug) {
        console.log('[Event Tracked]', name, data);
    }

    eventQueue.push(event);

    // Trim queue if too large (drop oldest)
    if (eventQueue.length > CONFIG.maxQueueSize) {
        eventQueue = eventQueue.slice(-CONFIG.maxQueueSize);
    }

    // Flush if batch size reached
    if (eventQueue.length >= CONFIG.batchSize) {
        flushEvents();
    }
};

/**
 * Send batched events to backend
 */
export const flushEvents = async (): Promise<void> => {
    if (eventQueue.length === 0) return;

    const eventsToSend = [...eventQueue];
    eventQueue = [];

    const batch: EventBatch = {
        events: eventsToSend,
        sentAt: new Date().toISOString(),
    };

    if (CONFIG.debug) {
        console.log('[Analytics] Flushing', eventsToSend.length, 'events to', CONFIG.apiUrl);
    }

    try {
        // Always use fetch for better error handling and CORS support
        // sendBeacon doesn't work well with CORS in development
        await sendWithFetch(batch);

        if (CONFIG.debug) {
            console.log('[Analytics] Successfully sent', eventsToSend.length, 'events');
        }
    } catch (error) {
        console.warn('[Analytics] Failed to send events:', error);
        // Re-queue events on failure (up to max size)
        eventQueue = [...eventsToSend, ...eventQueue].slice(-CONFIG.maxQueueSize);
    }
};

const sendWithFetch = async (batch: EventBatch): Promise<void> => {
    const response = await fetch(CONFIG.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
        keepalive: true,
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
};

/**
 * Initialize event tracking
 * Call once on app startup
 */
export const initEventTracking = (): void => {
    if (!CONFIG.enabled) {
        console.log('[Analytics] Event tracking disabled');
        return;
    }

    // Periodic flush
    flushTimer = setInterval(flushEvents, CONFIG.flushIntervalMs);

    // Flush on page hide/unload
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushEvents();
        }
    });

    window.addEventListener('beforeunload', () => {
        flushEvents();
    });

    console.log('[Analytics] Event tracking initialized', CONFIG.debug ? `(API: ${CONFIG.apiUrl})` : '');
};

/**
 * Stop event tracking and flush remaining events
 */
export const stopEventTracking = (): void => {
    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }
    flushEvents();
};

/**
 * Get current queue size (for debugging)
 */
export const getQueueSize = (): number => eventQueue.length;
